import React, { useState } from 'react';
import { AppBar, Toolbar, Container, Box, Button, IconButton, Drawer, List, ListItem, ListItemButton, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { NavLink } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import logo from '../../../img/nekrodruk-logo-dark.png';
import ThemeToggle from '../../ThemeToggle/ThemeToggle';

// Kolory to zmienne CSS z index.css – działają w trybie jasnym i ciemnym
const NAVY = 'var(--brand-navy)';
const ACCENT = 'var(--brand-navy)';
const FILL = 'var(--brand-navy-fill)';
const FILL_HOVER = 'var(--brand-navy-fill-deep)';
const LINE = 'var(--brand-line)';

const menuItems = [
    { label: 'Oferta', to: '/#Offer' },
    { label: 'Czym jest aplikacja?', to: '/#WhatIs' },
    { label: 'Jak korzystać?', to: '/#HowToUse' },
    { label: 'Poradniki', to: '/poradniki' },
    { label: 'Kontakt', to: '/#Contact' },
];

const pillBase = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '15px',
    borderRadius: '999px',
    px: 2.75,
    py: 1,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
};

const loginButtonSx = {
    ...pillBase,
    color: NAVY,
    border: `1.5px solid ${NAVY}`,
    background: 'transparent',
    '&:hover': { background: 'var(--brand-soft)', borderColor: NAVY, boxShadow: 'none' },
};

const registerButtonSx = {
    ...pillBase,
    color: '#fff',
    background: FILL,
    border: `1.5px solid ${FILL}`,
    '&:hover': { background: FILL_HOVER, borderColor: FILL_HOVER, boxShadow: 'none' },
};

export default function Nav() {
    const [menuOpen, setMenuOpen] = useState(false);

    const closeMenu = () => setMenuOpen(false);

    return(
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: 'var(--nav-bg)',
                    backdropFilter: 'blur(12px)',
                    borderBottom: `1px solid ${LINE}`,
                    color: NAVY,
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: { xs: 72, md: 88 } }}>
                        <Box component={NavLink} to="/" aria-label="Nekrodruk – strona główna" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <Box
                                component="img"
                                src={logo}
                                alt="Nekrodruk"
                                sx={{ height: { xs: 36, md: 44 } }}
                            />
                        </Box>
                        <Box component="nav" aria-label="Menu główne" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: { md: 2, lg: 3.5 } }}>
                            {menuItems.map(({ label, to }) => (
                                <Box
                                    key={to}
                                    component={HashLink}
                                    smooth
                                    to={to}
                                    sx={{
                                        position: 'relative',
                                        fontSize: '15px',
                                        fontWeight: 500,
                                        color: NAVY,
                                        textDecoration: 'none',
                                        whiteSpace: 'nowrap',
                                        py: 1,
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            left: 0,
                                            right: 0,
                                            bottom: 2,
                                            height: '2px',
                                            background: ACCENT,
                                            transform: 'scaleX(0)',
                                            transformOrigin: 'left',
                                            transition: 'transform 0.2s ease',
                                        },
                                        '&:hover::after, &:focus-visible::after': { transform: 'scaleX(1)' },
                                        '&:focus-visible': { outline: 'none' },
                                    }}
                                >
                                    {label}
                                </Box>
                            ))}
                            <Box sx={{ display: 'flex', gap: 1.25, ml: 1 }}>
                                <Button component={NavLink} to="/login" sx={loginButtonSx}>
                                    Zaloguj się
                                </Button>
                                <Button component={NavLink} to="/register" sx={registerButtonSx}>
                                    Załóż konto
                                </Button>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { md: 2 } }}>
                            <ThemeToggle />
                            <IconButton
                                edge="end"
                                onClick={() => setMenuOpen(true)}
                                sx={{ display: { xs: 'flex', md: 'none' }, color: NAVY }}
                                aria-label="Otwórz menu"
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="right"
                open={menuOpen}
                onClose={closeMenu}
                PaperProps={{
                    sx: {
                        width: 300,
                        maxWidth: '85vw',
                        backgroundColor: 'var(--surface)',
                        borderLeft: `3px solid ${ACCENT}`,
                        padding: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    },
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box component="img" src={logo} alt="Nekrodruk" sx={{ height: 32 }} />
                    <IconButton onClick={closeMenu} aria-label="Zamknij menu" sx={{ color: NAVY }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ borderColor: LINE }} />
                <List component="nav" aria-label="Menu główne" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {menuItems.map(({ label, to }) => (
                        <ListItem key={to} disablePadding>
                            <ListItemButton
                                component={HashLink}
                                smooth
                                to={to}
                                onClick={closeMenu}
                                sx={{
                                    borderRadius: 2,
                                    color: NAVY,
                                    fontSize: '16px',
                                    py: 1.25,
                                    '&:hover': { backgroundColor: 'var(--brand-soft)', color: ACCENT },
                                }}
                            >
                                {label}
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
                <Divider sx={{ borderColor: LINE }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
                    <Button component={NavLink} to="/register" onClick={closeMenu} sx={{ ...registerButtonSx, py: 1.25 }}>
                        Załóż konto
                    </Button>
                    <Button component={NavLink} to="/login" onClick={closeMenu} sx={{ ...loginButtonSx, py: 1.25 }}>
                        Zaloguj się
                    </Button>
                </Box>
            </Drawer>
        </>
    );
}
